import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button, Card, CardDescription, CardHeader, CardTitle, Mono, Text } from "@lagoon/ui";

import {
  ApiError,
  fetchSiteExport,
  type Deploy,
  type Site,
  type SiteExportArchive,
} from "../api.js";
import { formatBytes } from "../billing.js";
import { Notice, Working } from "./feedback.js";

/**
 * Taking your content away (LGN-145).
 *
 * `GET /api/sites/:id/export` has streamed a ZIP of a site's releases since
 * LGN-82, where it is the mechanism behind "you can always take your content
 * with you", and until this card no control in the product reached it. Being
 * unable to get your own files back out is not an ordinary missing feature, so
 * the control is on Settings rather than folded into the releases list: it is
 * a property of the site, not of one release.
 *
 * Two exports, because the route offers two and they answer different
 * questions. The live release unzips to exactly the folder that was uploaded,
 * which is what somebody moving to another host wants. `?all=1` is the whole
 * retained history, each release under its own folder, which is what somebody
 * leaving wants. The second one can be a gigabyte, so what it is about to do is
 * said before it is pressed rather than discovered.
 *
 * Everything this card can be told is shown here, beside the button that asked:
 * the limiter's refusal in the limiter's own words, a suspended site, a history
 * too large to build, a connection that broke mid-archive, and the one success
 * that is not entirely a success, which is a `?all=1` that ran out of page
 * (LGN-115). None of them are logged and swallowed, which is the failure mode
 * LGN-128 went through this application removing.
 */
export function SiteExport(props: { site: Site; deploys: Deploy[] }): React.ReactElement {
  /** The archive that landed, so the card can name the file it just saved. */
  const [saved, setSaved] = useState<SiteExportArchive | null>(null);
  /**
   * The caught failure rather than a rendered sentence, matching the deployer:
   * what to say about it is decided in one place, below, and holding the error
   * keeps the code available to decide with.
   */
  const [failure, setFailure] = useState<unknown>(null);

  /**
   * The live release's own row, when the page has it.
   *
   * `GET /api/sites/:id` returns the fifty most recent deploys, so a site whose
   * live release is older than that has a `publishedDeployId` and no row here.
   * That is why the button is enabled off the site record and only the size
   * caption comes from the row: a missing row is a fact about this page's
   * window, not about whether there is anything to export.
   */
  const live = props.deploys.find((deploy) => deploy.isPublished) ?? null;

  const download = useMutation({
    mutationFn: async (all: boolean) => {
      setFailure(null);
      setSaved(null);
      const archive = await fetchSiteExport(props.site, all);
      handToBrowser(archive);
      return archive;
    },
    onSuccess: (archive) => {
      setSaved(archive);
    },
    onError: (caught) => {
      setFailure(caught);
    },
  });

  return (
    <Card interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">Export</CardTitle>
          <CardDescription>
            Download this site&rsquo;s files as a ZIP, to keep or to take somewhere else. Nothing
            here changes: the live release stays live and nobody&rsquo;s access is affected.
          </CardDescription>
        </CardHeader>

        {failure === null ? null : <Notice tone="danger">{explainExportFailure(failure)}</Notice>}

        {saved === null ? null : saved.truncated ? (
          // The honest version of a partial success. Saying only "saved" here
          // would hand somebody one page of their history in a file named for
          // all of it (LGN-115).
          <Notice tone="warning">
            <Mono>{saved.filename}</Mono> is not the whole history of this site. It holds as many of
            the most recent releases as one export can carry, and <Mono>EXPORT-TRUNCATED.txt</Mono>{" "}
            inside the archive says so. Ask support if you need the older ones.
          </Notice>
        ) : (
          <Notice tone="success">
            Saved <Mono>{saved.filename}</Mono>.
          </Notice>
        )}

        <div className="lg:flex lg:flex-col lg:gap-4">
          <div className="lg:flex lg:flex-col lg:gap-1">
            <div>
              <Button
                variant="outline"
                disabled={download.isPending || props.site.publishedDeployId === null}
                onClick={() => {
                  download.mutate(false);
                }}
              >
                Export the live release
              </Button>
            </div>
            <Text size="caption" tone="subtle">
              {props.site.publishedDeployId === null
                ? "Nothing is live yet. Publish a release and this is what you get back."
                : live === null
                  ? "Unzips to exactly the folder that was uploaded."
                  : `Unzips to exactly the folder that was uploaded: ${String(live.fileCount)} files, ${formatBytes(live.totalBytes)}.`}
            </Text>
          </div>

          <div className="lg:flex lg:flex-col lg:gap-1">
            <div>
              <Button
                variant="outline"
                disabled={download.isPending}
                onClick={() => {
                  download.mutate(true);
                }}
              >
                Export every release
              </Button>
            </div>
            <Text size="caption" tone="subtle">
              Every release still retained for this site, each under its own folder. This can run to
              hundreds of megabytes and is built while you wait.
            </Text>
          </div>

          {download.isPending ? (
            <Working label="Building your archive. A large site takes a few minutes; keep this tab open until it saves." />
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/**
 * Hand the finished archive to the browser as a download.
 *
 * The bytes are already here: `fetchSiteExport` read the whole response so that
 * a refusal or a broken stream could be reported rather than saved as a short
 * ZIP. All that is left is an object URL and a synthetic click, which is the
 * only way to give a download a filename from script.
 */
function handToBrowser(archive: SiteExportArchive): void {
  const url = URL.createObjectURL(archive.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = archive.filename;
  // In the document, briefly: Safari ignores a click on a detached anchor. The
  // router's click handler skips anything carrying `download`, so this cannot
  // turn into a navigation.
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked later rather than immediately. The click starts a read of the blob
  // that outlives this function, and revoking under it cancels the save.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

/**
 * What to say about an export that did not happen.
 *
 * `ApiError` carries a sentence the platform wrote for a person, and every
 * refusal on this route has one worth repeating verbatim: the limiter's "too
 * many attempts", a suspended site's "contact support" (which is LGN-129: a
 * suspended site refuses the read as well as the write, so its owner cannot
 * export at all until that is decided), a history too large to build, and a
 * site with no live release. Rewriting any of them here would be this screen
 * guessing at a policy it does not own.
 *
 * The interrupted case is the one this file names, because it is the failure a
 * plain download link cannot report at all: the response was already 200 and
 * already flowing, and the point of the sentence is that nothing landed.
 */
function explainExportFailure(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  if (caught instanceof TypeError) {
    return "Could not reach the platform. Nothing was saved; check your connection and try again.";
  }
  return "The export failed. Nothing was saved, and nothing about this site changed.";
}
